import { DateCell } from "@components/common/date-cell";
import { AccessLevelDisplayNames } from "@components/access-level/access-level-utils";
import { GridRenderCellParams } from "@mui/x-data-grid-pro";
import { useMemo } from "react";
import { FaroTableColumnVisibilityMenuIcon } from "@components/common/faro-table/faro-table-column-visibility-menu-icon";
import { useColumnPreferencesContext } from "@context-providers/table/column-preferences-provider";
import { TableHeaderWithDraggableIcon } from "@components/table/table-header-with-draggable-icon";
import {
  SnapshotsTableColumnField,
  SnapshotsTableGridColDef,
} from "@pages/project-details/project-snapshots/snapshots-table-types";
import { SNAPSHOTS_TABLE_COLUMN_LABELS } from "@pages/project-details/project-snapshots/snapshots-table-constants";
import { isSnapshotsTableColumnField } from "@custom-types/type-guards";
import { Snapshot } from "@custom-types/base-types";
import { SnapshotName } from "@pages/project-details/project-snapshots/snapshot-name";
import { SnapshotOptions } from "@pages/project-details/project-snapshots/snapshot-options";
import { Box } from "@mui/material";

/**
 * @returns the columns definition to be used in the snapshots table
 */
export function useSnapshotsTableColumns(): SnapshotsTableGridColDef[] {
  const { columnPreferences, visibleOrderedColumns } =
    useColumnPreferencesContext();

  const columnsMap: Record<
    SnapshotsTableColumnField,
    SnapshotsTableGridColDef
  > = useMemo(() => {
    return {
      name: {
        field: "name",
        headerName: SNAPSHOTS_TABLE_COLUMN_LABELS.name,
        flex: 1,
        minWidth: 220,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by external package
        sortable: true,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        disableReorder: true,
        renderCell: (params: GridRenderCellParams<{ entity: Snapshot }>) => {
          return <SnapshotName snapshot={params.row.entity} />;
        },
      },

      created: {
        field: "created",
        headerName: SNAPSHOTS_TABLE_COLUMN_LABELS.created,
        minWidth: 22,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by external package
        sortable: true,
        renderHeader: (params) => (
          <TableHeaderWithDraggableIcon
            title={params.colDef.headerName ?? ""}
          />
        ),
        renderCell: (params: GridRenderCellParams<{ entity: Snapshot }>) => {
          return (
            <Box px={1}>
              <DateCell date={params.row.entity.createdAt} />
            </Box>
          );
        },
      },

      accessLevel: {
        field: "accessLevel",
        headerName: SNAPSHOTS_TABLE_COLUMN_LABELS.accessLevel,
        minWidth: 180,
        renderHeader: (params) => (
          <TableHeaderWithDraggableIcon
            title={params.colDef.headerName ?? ""}
          />
        ),
        renderCell: (params: GridRenderCellParams<{ entity: Snapshot }>) => {
          const accessLevel = params.row.entity.accessLevel;
          return <Box px={1}>{AccessLevelDisplayNames[accessLevel]}</Box>;
        },
        // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by external package
        sortable: true,
      },

      options: {
        field: "options",
        type: "actions",
        align: "right",
        width: 90,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        disableReorder: true,
        renderHeader: () => (
          <FaroTableColumnVisibilityMenuIcon
            columnPreferences={columnPreferences}
          />
        ),
        renderCell: (params: GridRenderCellParams<{ entity: Snapshot }>) => {
          return <SnapshotOptions snapshot={params.row.entity} />;
        },
        // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by external package
        sortable: false,
      },
    };
  }, [columnPreferences]);

  const orderedColumns = visibleOrderedColumns
    .filter(isSnapshotsTableColumnField)
    .map((header) => columnsMap[header]);

  return orderedColumns;
}
